import React from 'react';
import { Handshake, Star } from 'lucide-react';

const Sponsors = ({ sponsors }) => {
  // Separa i partner per tier
  const mainSponsors = sponsors.filter(s => s.tier === 'main');
  const premiumSponsors = sponsors.filter(s => s.tier === 'premium');
  const technicalSponsors = sponsors.filter(s => s.tier === 'technical');

  return (
    <section className="py-20 bg-black">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
            I Nostri <span className="text-cyan-400">Partner</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-yellow-400 mx-auto mb-6"></div>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Insieme verso il successo
          </p>
        </div>

        {/* Main Sponsors */}
        {mainSponsors.length > 0 && (
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white text-center mb-8 flex items-center justify-center gap-3">
              <Star className="w-8 h-8 text-yellow-400" />
              Main Partner
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {mainSponsors.map((sponsor) => (
                <div
                  key={sponsor.id}
                  className="group bg-gradient-to-br from-cyan-500/10 to-yellow-500/10 p-12 rounded-2xl border-2 border-cyan-500/50 hover:border-cyan-500 transition-all duration-300 flex flex-col items-center justify-center text-center hover:transform hover:-translate-y-2"
                >
                  {sponsor.logo ? (
                    <div className="w-full h-32 flex items-center justify-center mb-6">
                      <img loading="lazy" decoding="async" 
                        src={sponsor.logo} 
                        alt={sponsor.name}
                        className={`max-w-full max-h-full object-contain transition-transform duration-300 ${
                          sponsor.name === 'AC Management' ? 'scale-125' : ''
                        }`}
                        style={
                          sponsor.name === 'AC Management' 
                            ? { 
                                filter: 'invert(1) hue-rotate(180deg) saturate(2)',
                                transform: 'scale(1.25)'
                              }
                            : {}
                        }
                        onMouseEnter={(e) => {
                          if (sponsor.name === 'AC Management') {
                            e.currentTarget.style.transform = 'scale(1.35)';
                          } else {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (sponsor.name === 'AC Management') {
                            e.currentTarget.style.transform = 'scale(1.25)';
                          } else {
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/30">
                      <Handshake className="w-12 h-12 text-black" />
                    </div>
                  )}
                  <h3 className="text-3xl font-black text-white mb-2">{sponsor.name}</h3>
                  <p className="text-cyan-400 text-sm font-semibold uppercase tracking-wide">
                    {sponsor.category}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Premium Sponsors */}
        {premiumSponsors.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-300 text-center mb-8">Premium Partner</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 max-w-6xl mx-auto">
              {premiumSponsors.map((sponsor) => (
                <div
                  key={sponsor.id}
                  className="group bg-gradient-to-br from-gray-900 to-gray-800 p-8 rounded-xl border border-gray-700 hover:border-cyan-500/50 transition-all duration-300 flex flex-col items-center justify-center text-center hover:transform hover:-translate-y-2"
                >
                  {sponsor.logo ? (
                    <div className="w-full h-16 flex items-center justify-center mb-4">
                      <img loading="lazy" decoding="async" 
                        src={sponsor.logo} 
                        alt={sponsor.name}
                        className="max-w-full max-h-full object-contain transition-transform duration-300"
                        style={
                          sponsor.name === 'I Quintili'
                            ? { filter: 'brightness(0) invert(1)', transform: 'scale(1)' }
                            : {}
                        }
                        onMouseEnter={(e) => {
                          if (sponsor.name === 'I Quintili') {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          } else {
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-cyan-500/20">
                      <Handshake className="w-8 h-8 text-black" />
                    </div>
                  )}
                  <h4 className="text-lg font-bold text-white mb-1">{sponsor.name}</h4>
                  <p className="text-cyan-400 text-xs font-semibold uppercase tracking-wide">
                    {sponsor.category}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Technical Sponsors */}
        {technicalSponsors.length > 0 && (
          <div>
            <h3 className="text-2xl font-bold text-gray-300 text-center mb-8">Technical Partner</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
              {technicalSponsors.map((sponsor) => (
                <div
                  key={sponsor.id}
                  className="group bg-gradient-to-br from-gray-900 to-gray-800 p-6 rounded-lg border border-gray-700 hover:border-cyan-500/50 transition-all duration-300 flex flex-col items-center justify-center text-center hover:transform hover:-translate-y-1"
                >
                  {sponsor.logo ? (
                    <div className="w-full h-12 flex items-center justify-center mb-3">
                      <img loading="lazy" decoding="async" 
                        src={sponsor.logo} 
                        alt={sponsor.name}
                        className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110"
                        style={
                          ['Raven', 'Alpinestars', 'InnTeck', 'Mika Metals', 'Il Ruotino', '24mx', 'LRX', 'Renox', 'Hurly'].includes(sponsor.name)
                            ? { filter: 'brightness(0) invert(1)' }
                            : {}
                        }
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg flex items-center justify-center mb-3 group-hover:bg-gradient-to-br group-hover:from-cyan-500 group-hover:to-cyan-600 transition-all duration-300">
                      <Handshake className="w-6 h-6 text-white group-hover:text-black transition-colors duration-300" />
                    </div>
                  )}
                  <h5 className="text-sm font-bold text-white mb-1 leading-tight">{sponsor.name}</h5>
                  <p className="text-gray-400 text-xs uppercase tracking-wide">
                    {sponsor.category}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-6 text-lg">
            Vuoi diventare un nostro partner?
          </p>
          <a 
            href="mailto:alessandro.casalini@sevenmotorsport.it,simone.paolucci@sevenmotorsport.it"
            className="inline-block px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-black font-bold text-lg rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg shadow-cyan-500/30"
          >
            Contattaci
          </a>
        </div>
      </div>
    </section>
  );
};

export default Sponsors;
